<template>
  <div class="list impact-list">
    <div class="label mt-0">
      <template v-if="type == 'outgoing'">
        {{ $t('labels.outgoingRelations') }}
      </template>
      <template v-else>
        {{ $t('labels.incomingRelations') }}
      </template>
    </div>

    <item-combo-select
      key="target-menu-incomingselect"
      :instruction="$t('texts.chooseIncomingItems')"
      :available-ids="selectableElementIds"
      :targets="targets"
      :resources="resources"
      :indices="indices"
      :selection="connectedElements"
      :hide-selected="true"
      :disabled-item-uids="new Set([props.elementUid])"
      :fixed-element="{ id: baseElement.id, type: baseElement.type, uid: baseElement.uid }"
      :placeholder="`+ ${$t('labels.new')}`"
      @select-single="addElement"
      @choose="onElementsChosen"
    />

    <sortable-list
      class="impact-list__list"
      line-class="impact-list__listline"
      ghost-list-class="impact-list__ghostlist"
      :items="impacts"
      :order="impacts.map((i) => i.id)"
      gap="0.25rem"
      @update:order="impactStore.saveOrder($event)"
    >
      <template #default="{ item: impact }">
        <relation-list-line
          :type="type"
          :relation="impact"
          :editable="editable"
          :deletable="deletable"
          :selected="impact.id == selectedRelationId"
          class="impact-list__line"
          @select="selectImpact"
        />
      </template>
    </sortable-list>

    <div class="impact-list__before-sum" v-if="noneImpacts.length > 0">
      <details class="mt-2 mb-1 pl-4">
        <summary style="color: var(--color-text-medium)">{{ $t("labels.noneRelations") }}</summary>
        <button-list
          :items="noneImpacts"
          class="mt-2"
          :selected-item="selectedRelationId != null ? { type: 'relation', id: selectedRelationId } : null"
          :deletable="true"
          @delete="removeImpact($event)"
          @select="emit('select', $event)"
        >
          <template #default="{ item: nonImpact }">
            <store-item
              :id="nonImpact.startItemId"
              :type="nonImpact.startItemType"
            ></store-item>
          </template>
        </button-list>
      </details>
    </div>

    <div v-if="impacts.length > 0" class="impact-list__sum">
      <span class="impact-list__sum__title label">{{
        $t("labels.totalStrength")
      }}</span>
      <span class="impact-list__sum__value" :title="$n(totalInfluence)">
        {{ $n(totalInfluence, { notation: "compact" }) }}
      </span>
    </div>
    
    <div v-if="$slots['after']">
      <slot name="after"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { map } from "lodash-es";

import SortableList from "@core/ui/SortableList.vue";
import RelationListLine from "./RelationListLine.vue";
import ItemComboSelect from "@editor/ui/ItemComboSelect.vue";
import ButtonList from "@core/ui/ButtonList.vue";
import StoreItem from "@editor/items/StoreItem.vue";

import { useRelationStore } from "./relations.store";
import { useResourceStore } from "../resource/resources.store";
import { useTargetStore } from "../target/targets.store";
import { useIndexStore } from "../index/indices.store";
import { useElements } from "../element/elements";

import type { RelationItem } from "./RelationItem";

const props = withDefaults(defineProps<{
  type: "incoming" | "outgoing";
  elementUid: `target_${number}` | `resource_${number}` | `index_${number}`;
  editable?: boolean;
  selectedRelationId?: number;
  deletable?: boolean;
}>(), {
  editable: true,
  deletable: true
});

const emit = defineEmits<{
  (e: "select", impact: RelationItem | null): void;
}>();

const impactStore = useRelationStore();

const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const targets = computed(() => {
  return useTargetStore().items;
});
const elements = useElements();

function selectImpact(impact: RelationItem | null) {
  emit("select", impact);
}

const baseElement = computed(() => {
  return elements.byUid[props.elementUid];
});

// ===================
// IMPACTS

const impacts = computed(() => {
  return props.type === "outgoing"
    ? outgoingImpacts.value
    : incomingImpacts.value;
});

const connectedElements = computed(() => {
  return props.type === "outgoing"
    ? connectedOutgoingItems.value
    : connectedIncomingItems.value;
});

const noneImpacts = computed(() => props.type === "outgoing"
  ? outgoingNonImpacts.value
  : incomingNonImpacts.value);


const selectableElementIds = computed(() => {
  return props.type === "outgoing"
    ? selectableOutgoingElementIds.value
    : selectableIncomingElementIds.value;
});

const totalInfluence = computed(() => {
  return impacts.value.reduce(
    (sum, impact) => sum + impact.maxInfluence,
    0
  );
});


// INCOMING

const incomingImpacts = computed(() => {
  return impactStore.activeByEndUid[props.elementUid] ?? [];
});

const incomingNonImpacts = computed(() => {
  return (impactStore.byEndUid[props.elementUid] ?? []).filter(
    (rel) => rel.updateType === "none"
  );
});

const connectedIncomingItems = computed(() => {
  const incomingImpacts = impactStore.byEndUid[props.elementUid] ?? [];
  return incomingImpacts
    .map((rel) => {
      if (rel.startItemType === "target") {
        return targets.value[rel.startItemId];
      } else if (rel.startItemType === "index") {
        return indices.value[rel.startItemId];
      } else {
        return resources.value[rel.startItemId];
      }
    });
});

const selectableIncomingElementIds = computed(() => {
  return {
    target: map(targets.value, (t) => t.id),
    resource: map(resources.value, (r) => r.id),
    index: map(indices.value, (i) => i.id)
  };
});

// OUTGOING

const outgoingImpacts = computed(() => {
  return impactStore.activeByStartUid[props.elementUid] ?? [];
});

const outgoingNonImpacts = computed(() => {
  return (impactStore.byStartUid[props.elementUid] ?? []).filter(
    (rel) => rel.updateType === "none"
  );
});

const connectedOutgoingItems = computed(() => {
  const outgoingImpacts = impactStore.byStartUid[props.elementUid] ?? [];
  return outgoingImpacts.map((rel) => {
    if (rel.endItemType === "target") {
      return targets.value[rel.endItemId];
    } else {
      return resources.value[rel.endItemId];
    }
  });
});

const selectableOutgoingElementIds = computed(() => {
  return {
    target: map(targets.value, (t) => t.id),
    resource: map(resources.value, (r) => r.id),
    index: []
  };
});

async function onIncomingChosen(
  items: {
    id: number;
    type: "target" | "resource" | "index";
  }[]
) {
  const newImpacts = await impactStore.updateIncomingConnections(items, {
    endItemType: baseElement.value.type,
    endItemId: baseElement.value.id
  });

  if (newImpacts.length > 0) {
    selectImpact(newImpacts[0]);
  }
}

async function addIncomingItem(item: {
  type: "target" | "resource" | "index";
  id: number;
}) {
  if (baseElement.value.type == "index") return;

  const impact = await impactStore.create({
    startItemType: item.type,
    startItemId: item.id,
    endItemType: baseElement.value.type,
    endItemId: baseElement.value.id
  });
  selectImpact(impact);
}

async function onOutgoingChosen(
  items: { type: "target" | "resource"; id: number }[]
) {
  const newImpacts = await impactStore.updateOutgoingConnections(items, {
    startItemType: baseElement.value.type,
    startItemId: baseElement.value.id
  });

  if (newImpacts.length > 0) {
    selectImpact(newImpacts[0]);
  }
}

async function addOutgoingItem(item: {
  type: "target" | "resource";
  id: number;
}) {
  const impact = await impactStore.create({
    endItemType: item.type,
    endItemId: item.id,
    startItemType: baseElement.value.type,
    startItemId: baseElement.value.id
  });
  selectImpact(impact);
}

function addElement(item: { type: "target" | "resource" | "index"; id: number }) {
  if (props.type === "outgoing") {
    if (isNotIndex(item)) {
      addOutgoingItem(item);
    }
  } else {
    addIncomingItem(item);
  }
}

function onElementsChosen(items: { id: number; type: "target" | "resource" | "index" }[]) {
  if (props.type === "outgoing") {
    onOutgoingChosen(items.filter(isNotIndex));
  } else {
    onIncomingChosen(items);
  }
}

function removeImpact(impact: RelationItem) {
  emit("select", null);
  impactStore.delete(impact);
}

function isNotIndex<T extends { type: "target" | "resource" | "index" }>(item: T): item is T & { type: "target" | "resource" } {
  return item.type !== "index";
}
</script>

<style lang="scss">
.impact-list {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
  align-items: center;
  min-width: 0;

  & > * {
    grid-column: 1 / -1;
  }

  &__list {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
    grid-template-columns: subgrid;
    column-gap: 0.5rem;
  }
  &__ghostlist {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
    column-gap: 0.5rem;
    background-color: var(--color-background-deep);
  }
  &__listline {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
    grid-template-columns: subgrid;
    column-gap: 0.5rem;

    &.-is-ghost {
      grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
    }
  }

  &__line {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    padding-right: 0.5rem;
  }

  &__sum {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 2.25em fit-content(3.5rem);
    grid-template-columns: subgrid;
    align-items: center;
    grid-gap: 0.25em;
    min-width: 0;
    padding-top: 0.5em;
    padding-bottom: 0.5em;
    margin-top: 0.125em;
    border-top: 1px solid var(--color-border-light);

    &__title {
      text-align: right;
      margin: 0;
      grid-column: 1 / 3;
    }
    &__value {
      grid-column: 3 / 4;
      padding-right: 0.5rem;
      text-align: right;
      font-weight: 600;
      min-width: 3.5rem;
    }
  }
}
</style>
